import React, { useEffect, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import 'objectFitPolyfill';

import { selectLocale } from 'models/localization';
import Routes from 'Routes';
import BannerView from '../../BannerTemplateSwitch';
import PageEntry from './PageEntry';
import LobbyFooterSwitch from '../../LobbyFooterSwitch';

const RootContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const TopLogoContainer = styled.div<{ total: number }>`
  background: #DA4437;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 85px;

  & > img {
    max-width: 100%;
    max-height: 100%;
    width: ${({ total }) => `calc(100% / ${total + 1})`};
    height: auto;
    object-fit: contain;
    margin: 0 20px;
  }
`;

const BackgroundVideo = styled.video`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  width: 100%;
  margin-top: -100px;
  z-index: -1;
`;

const StyledBackgroundImage = styled.img`
  width: 100%;
  margin-top: -100px;
  z-index: -1;
`;

const ContentContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  bottom: 0;
`;

const Banner = styled(BannerView)`
  position: absolute;
  left: ${({ left }) => left}%;
  top: ${({ top }) => top}%;
  width: ${({ width }) => width}%;
  height: ${({ height }) => height}%;
  flex: 1 0 auto;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
`;

const PageEntryTextContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const PageEntryText = styled.div`
  font-size: 1.9vw;
`;

const SmallPageEntryText = styled.div<{ locale: string }>`
  font-size: ${({ locale }) => (locale === 'ja' ? '1vw' : '2vw')};
`;

const GoToExhibitionWebsiteContainer = styled.div`
  color: #484848;
  font-size: 1.5vw;
`;

const StyledArrowForwardIosIcon = styled(ArrowForwardIosIcon)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const FooterWrapper = styled.div`
  position: absolute;
  transform: translateX(-50%);
  left: 50%;
  bottom: 0;
`;

const DesktopLobbyViewReedTemplate = () => {
  const locale = useSelector(selectLocale);
  const { t } = useTranslation(['lobby', 'landing', 'common']);
  const backgroundVideoSrc = 'https://contents-virtual.eventxtra.com/uploads/reed902_staging/originals/c2f087d5-bb9e-4ae3-b107-00337fca37d2.mp4';
  const backgroundPreviewSrc = 'https://contents-virtual.eventxtra.com/uploads/reed902_staging/originals/49311270-4e8d-4abb-97a6-fdb839f73d21.jpg';

  const autoPlay = { left: true, middle: true, right: true };
  const banners: any = (t('banners', '', { returnObjects: true }) || {});

  useEffect(() => {
    window.objectFitPolyfill();
  }, []);

  return (
    <RootContainer>
      <ContentContainer>
        <StyledBackgroundImage src={backgroundPreviewSrc} />
        <BackgroundVideo autoPlay loop muted>
          <source src={backgroundVideoSrc} type="video/mp4" />
        </BackgroundVideo>
        {banners?.left?.length > 0 && (
          <Banner
            left={20}
            top={8}
            height={20}
            width={20}
            assets={banners.left}
            autoPlay={autoPlay.left}
          />
        )}
        {banners?.right?.length > 0 && (
          <Banner
            left={80}
            top={8}
            height={20}
            width={20}
            assets={banners.right}
            autoPlay={autoPlay.right}
          />
        )}
        {banners?.middle?.length > 0 && (
          <Banner
            left={50}
            top={5}
            height={25}
            width={25}
            assets={banners?.middle}
            autoPlay={autoPlay.middle}
          />
        )}
        <PageEntry
          left={30}
          top={40}
          height={15}
          width={25}
          bgColor="#0E7ABC"
          link={Routes.hall}
        >
          <PageEntryText>
            <Trans
              defaults="To Exhibition Hall"
              i18nKey="lobby:entry.to_exhibition_hall"
            />
          </PageEntryText>
          <StyledArrowForwardIosIcon />
        </PageEntry>
        <PageEntry
          left={70}
          top={40}
          height={15}
          width={25}
          bgColor="#0CA58B"
          link={Routes.custom}
        >
          <PageEntryTextContainer>
            <Trans
              defaults="How to utilize virtual expo?"
              i18nKey="lobby:entry.how_to_use_oms"
              components={{
                b: <PageEntryText />,
                i: <SmallPageEntryText locale={locale} />,
              }}
            />
          </PageEntryTextContainer>
          <StyledArrowForwardIosIcon />
        </PageEntry>
        <PageEntry
          left={50}
          top={60}
          height={5}
          width={27}
          bgColor="#FFF"
          link={t('common:url.exhibition_website')}
          isPopupLink
        >
          <GoToExhibitionWebsiteContainer>
            <Trans
              defaults="Go to Exhibition Website >>"
              i18nKey="landing:link.exhibition"
            />
          </GoToExhibitionWebsiteContainer>
        </PageEntry>
        <FooterWrapper>
          <Suspense fallback={null}>
            <LobbyFooterSwitch />
          </Suspense>
        </FooterWrapper>
      </ContentContainer>
    </RootContainer>
  );
};

export default DesktopLobbyViewReedTemplate;
